import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { generateReceipt, previewReceipt } from '../../api/payments';
import { ExchangeRateSelect } from '../../components/ExchangeRateSelect';
import { ExchangeRate } from '../../types/exchangeRate';
import { DocumentCurrency, Payment, ReceiptDocument } from '../../types/payment';
import { formatMoney, formatRate, formatRateDateTime, rateOriginLabel } from '../../utils/exchangeRates';

const errorText = (err: unknown, fallback: string) =>
  isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;

/** Generate Receipt (Phase 104) — choose USD or LKR and the USD/LKR rate,
 * check the server-calculated preview (amounts, rate, its date and source),
 * then generate. Each generation is saved as its own immutable receipt
 * version; the payment and earlier versions never change. The default rate
 * is the one the payment was recorded at. */
export const GenerateReceiptDialog = ({
  payment,
  canChooseRate,
  onClose,
  onGenerated,
}: {
  payment: Payment | null;
  canChooseRate: boolean;
  onClose: () => void;
  onGenerated: (payment: Payment, document: ReceiptDocument) => void;
}) => {
  const [currency, setCurrency] = useState<DocumentCurrency>('LKR');
  const [rate, setRate] = useState<ExchangeRate | null>(null);
  const body = { currency, ...(rate ? { exchangeRateId: rate.id } : {}) };

  const preview = useQuery({
    queryKey: ['receipt-preview', payment?.id, body],
    queryFn: () => previewReceipt(payment!.id, body),
    enabled: !!payment,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const generate = useMutation({
    mutationFn: () => generateReceipt(payment!.id, body),
    onSuccess: (document) => {
      onGenerated(payment!, document);
      close();
    },
  });

  const close = () => {
    if (generate.isPending) return;
    setCurrency('LKR');
    setRate(null);
    generate.reset();
    onClose();
  };

  const paymentRate: ExchangeRate | null =
    payment?.usedExchangeRate && payment.exchangeRate
      ? ({
          id: payment.usedExchangeRate.id,
          rate: payment.exchangeRate,
          origin: payment.usedExchangeRate.origin,
          sourcePublishedAt: payment.usedExchangeRate.sourcePublishedAt,
          source: payment.usedExchangeRate.origin === 'MANUAL' ? 'Manual' : 'Nations Trust Bank',
        } as ExchangeRate)
      : null;

  const p = preview.data;
  return (
    <Dialog open={!!payment} onClose={close} fullWidth maxWidth="sm" aria-labelledby="generate-receipt-title">
      <DialogTitle id="generate-receipt-title">
        Generate Receipt {payment?.receipt?.receiptNumber ? `— ${payment.receipt.receiptNumber}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {payment?.paymentNumber} — {payment?.category.name}. Choose the receipt currency and the exchange rate to
            print. A new receipt version is saved; earlier versions and the payment itself don't change.
          </Typography>
          <RadioGroup row value={currency} onChange={(e) => setCurrency(e.target.value as DocumentCurrency)}>
            <FormControlLabel value="USD" control={<Radio />} label="USD receipt" />
            <FormControlLabel value="LKR" control={<Radio />} label="LKR receipt" />
          </RadioGroup>
          <ExchangeRateSelect
            value={rate}
            onChange={setRate}
            canChoose={canChooseRate}
            defaultRate={paymentRate ?? undefined}
            defaultLabel={paymentRate ? 'Rate this payment was recorded at' : 'Current automatic rate'}
          />
          {preview.isError && <Alert severity="error">{errorText(preview.error, "Couldn't calculate the receipt.")}</Alert>}
          {p && (
            <Box data-testid="receipt-preview" sx={{ opacity: preview.isFetching ? 0.6 : 1 }}>
              <Alert severity={p.exchangeRate != null ? 'info' : 'warning'} sx={{ mb: 1 }}>
                {p.exchangeRate != null ? (
                  <>
                    Rate used: <strong>Rs. {formatRate(p.exchangeRate)}</strong> per USD — effective{' '}
                    {p.exchangeRateDate ? formatRateDateTime(p.exchangeRateDate) : '—'} —{' '}
                    {rateOriginLabel(p.exchangeRateOrigin)}
                  </>
                ) : (
                  'No exchange rate applies.'
                )}
              </Alert>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Payment ({p.paymentCurrency})</TableCell>
                    <TableCell align="right">
                      {formatMoney(p.amount)}
                      {p.taxAmount ? ` + SSCL ${formatMoney(p.taxAmount)}` : ''} = {formatMoney(p.totalAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>USD</TableCell>
                    <TableCell align="right">{p.amountUsd != null ? formatMoney(p.amountUsd) : '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>LKR</TableCell>
                    <TableCell align="right">{p.amountLkr != null ? formatMoney(p.amountLkr) : '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Receipt amount</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        {p.currency} {formatMoney(p.receiptAmount)}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
          {!p && preview.isFetching && (
            <Typography variant="body2" color="text.secondary">
              Calculating…
            </Typography>
          )}
          {generate.isError && <Alert severity="error">{errorText(generate.error, "Couldn't generate the receipt.")}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={close} disabled={generate.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!p || preview.isFetching || generate.isPending}
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? 'Generating…' : `Generate ${currency} receipt`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
